import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';

import { Logger } from 'utils/Logger';
import { handleApiError } from 'utils/ErrorHandler';
import { API_BASE_URL } from 'envConfig';

interface CompanyData {
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  country: string;
  zip: string;
  companyEmail?: string;
  companyPhone?: string;
  website?: string;
}

interface CompanyDetailsProps {
  companyId: string;
  onCompanyDataLoaded?: (companyName: string) => void;
  onCompanyError?: (error: string) => void;
}

export const CompanyDetails: React.FC<CompanyDetailsProps> = ({ companyId, onCompanyDataLoaded, onCompanyError }) => {
  const theme = useTheme();
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanyData = async () => {
    setLoading(true);
    setError(null);
    
    const url = `${API_BASE_URL}/public/company/${companyId}`;
    Logger.debug('CompanyDetails.tsx', `Making API request to: ${url}`);
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });


      
      if (!response.ok) {
        const errorMessage = await handleApiError(response);
        throw new Error(`Failed to fetch company data: ${errorMessage}`);
      }

      const data = await response.json();
      setCompanyData(data.data);
      
      // Call the callback with company name if provided
      if (onCompanyDataLoaded && data.data.name) {
        onCompanyDataLoaded(data.data.name);
      }
      
      Logger.debug('CompanyDetails.tsx', `Company data fetched successfully for company ID: ${companyId}`);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      Logger.error('CompanyDetails.tsx', `Failed to fetch company data: ${errorMessage}`);
      
      // Call the error callback if provided
      if (onCompanyError) {
        onCompanyError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
    }
  }, [companyId]);

  const formatAddress = (company: CompanyData) => {
    const parts = [
      company.addressLine1,
      company.addressLine2,
      company.city,
      company.state,
      company.country,
      company.zip
    ].filter(Boolean);
    
    return parts.join(', ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!companyData) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        Company information not found
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '1920px',
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: '800px',
          borderRadius: 3,
          bgcolor: theme.palette.mode === 'dark' 
            ? 'rgb(20, 25, 55)' 
            : theme.palette.grey[100],
          border: `1px solid ${theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.12)' 
            : theme.palette.grey[300]}`,
          boxShadow: theme.shadows[2],
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Company Name Header */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold',
                color: theme.palette.secondary.main,
                mb: 2,
              }}
            >
              {companyData.name}
            </Typography>
            
            {/* Company Details - Centered */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
              {/* Address */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon 
                  sx={{ 
                    color: theme.palette.secondary.main,
                    fontSize: '1rem',
                  }} 
                />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {formatAddress(companyData)}
                </Typography>
              </Box>

              {/* Email */}
              {companyData.companyEmail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon 
                    sx={{ 
                      color: theme.palette.secondary.main,
                      fontSize: '1rem',
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {companyData.companyEmail}
                  </Typography>
                </Box>
              )}

              {/* Phone */}
              {companyData.companyPhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon 
                    sx={{ 
                      color: theme.palette.secondary.main,
                      fontSize: '1rem',
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    {companyData.companyPhone}
                  </Typography>
                </Box>
              )}

              {/* Website */}
              {companyData.website && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WebsiteIcon 
                    sx={{ 
                      color: theme.palette.secondary.main,
                      fontSize: '1rem',
                    }} 
                  />
                  <Typography 
                    variant="body2" 
                    component="a"
                    href={companyData.website.startsWith('http') ? companyData.website : `https://${companyData.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      textDecoration: 'none',
                      color: theme.palette.secondary.main,
                      textAlign: 'center',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    {companyData.website}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}; 